import { CheckCircleOutline } from '@mui/icons-material';
import Button from '@mui/material/Button';
import ArrowIcon from 'src/components/icons/Arrow';
import useAppContext from 'src/hooks/useAppContext';
import usePolarisActor from 'src/hooks/usePolarisActor';
import useResults from 'src/interviews/hooks/useResults';
import TreatmentSupplierIconComponent from '../../Completion/components/TreatmentSupplierIcon';
import { isWeekend } from '../utils';

import type { TreatmentSupplierIcon } from 'src/packages/polaris/types';

type Props = {
  onButtonClick: () => void;
  chargeAmount: number;
};

const bulletPoints: (
  isWeekend: boolean,
) => Array<{ icon: TreatmentSupplierIcon; label: string }> = (isWeekend) => [
  { icon: 'teleMedicine', label: 'Reviewed by a licensed physician' },
  {
    icon: 'rxBottle',
    label: `Get your results confirmed ${isWeekend ? 'by Monday' : 'in under 24 hours'}`,
  },
  { icon: 'star', label: '95% satisfaction rating' },
];

const DoctorConfirmationEntryCard = ({ onButtonClick, chargeAmount }: Props) => {
  const { token: resultsToken } = useResults() || {};
  const actor = usePolarisActor();
  const { isOpen: isPolarisAppOpen } = useAppContext();
  const { doctorConfirmationRequestedInterviews, identifiers } =
    (actor as any)?.parent?.state?.context || {};

  const alreadySubmitted = doctorConfirmationRequestedInterviews.includes(resultsToken);
  const containerClassName = alreadySubmitted ? 'bp-blur-sm bp-p-2' : '';

  return (
    <>
      {alreadySubmitted && (
        <div className="bp-absolute bp-bottom-0 bp-left-0 bp-right-0 bp-top-0 bp-z-10 bp-flex bp-flex-col bp-items-center bp-justify-center bp-bg-[rgba(255,255,255,0.6)] bp-p-12 bp-text-center">
          <CheckCircleOutline color="success" fontSize="large" />
          <p className="bp-font-primary-main">We got your request, {identifiers.firstName}!</p>
          <p className="bp-font-primary-small bp-mt-2">
            A doctor will review your transcript and email their feedback to:{' '}
            <span className="bp-underline">{identifiers.email}</span>.
          </p>
        </div>
      )}
      <div className={containerClassName}>
        <p className="bp-font-primary-main bp-my-0 bp-font-bold">
          Confirm your case with a doctor for ${chargeAmount.toFixed(2)}
        </p>
        <p className="bp-font-primary-smallest bp-mb-1.5 bp-mt-0 bp-text-gray-70">
          Powered by OptimalMD
        </p>
        <div className="bp-font-primary-smaller bp-my-2 bp-text-gray-70">
          <span className="bp-text-sm bp-font-bold bp-text-status-success">
            ${chargeAmount.toFixed(2)}
          </span>{' '}
          <s>&nbsp;$29.00&nbsp;</s>
        </div>
        <div className="bp-my-2 bp-mb-4">
          <div className="bp-font-primary-smaller bp-grid bp-gap-2 bp-text-gray-70">
            {bulletPoints(isWeekend()).map((bp) => (
              <div className="bp-flex bp-items-start" key={bp.icon}>
                <TreatmentSupplierIconComponent
                  type={bp.icon}
                  containerClassName="!bp-bg-transparent !bp-p-0"
                  iconClassName="!bp-h-6 !bp-w-6"
                />
                <span className="bp-ml-2 bp-mt-1">{bp.label}</span>
              </div>
            ))}
          </div>
        </div>
        <Button
          className="bp-flex bp-w-full bp-items-center bp-justify-between bp-rounded-[1.875rem] bp-bg-orange-20 bp-px-6 bp-py-1 hover:!bp-bg-orange-30"
          disabled={alreadySubmitted}
          onClick={onButtonClick}
          fullWidth={isPolarisAppOpen}
          sx={
            alreadySubmitted
              ? {}
              : {
                  boxShadow:
                    '0px 1px 3px 0px rgba(0, 0, 0, 0.20), 0px 2px 1px -1px rgba(0, 0, 0, 0.12), 0px 1px 1px 0px rgba(0, 0, 0, 0.14)',
                }
          }
        >
          <span className="bp-mr-4 bp-text-sm bp-normal-case bp-text-gray-100">
            Confirm my results
          </span>
          <ArrowIcon />
        </Button>
      </div>
    </>
  );
};

export default DoctorConfirmationEntryCard;
